import type { V } from "@utils/types";
import { findSelectOption } from "./select";
import { computed, ref, toRef, watch, type ComputedRef, type Ref } from "vue";
import { useSearch } from "@utils/search";
import type { PathHelper } from "@js-from-routes/client";
import { useErrorToast } from "@utils/toasts";

export type SelectOptionsOrProvider <T> = SelectOptionsProvider<T> | V.Select.Option<T>[]

export function toSelectOptionsProvider <T> (it: Ref<SelectOptionsProvider<T> | V.Select.Option<T>[]>) {
  if (it.value instanceof SelectOptionsProvider) {
    return it.value
  } else {
    return new StaticSelectOptionsProvider({ options: it as Ref<V.Select.Option<T>[]> })
  }
}

export type UsedSelectOptionsProvider<T> = ReturnType<SelectOptionsProvider<T>['use']>

export abstract class SelectOptionsProvider <T> {
  protected options = ref<V.Select.Option<T>[]>([]) as Ref<V.Select.Option<T>[]> // Over-typed ref to ensure it is not stuck on un-ref hell
  protected sort = ref<'asc' | 'desc'>()
  protected search = ref('')

  /**
   * Array of options
   */
  protected allOptions = computed(() => this.options.value)

  /**
   * Array of options scoped to the current search & sort settings
   */
  protected abstract scopedOptions: ComputedRef<V.Select.Option<T>[]>

  /**
   * Loads next page for options
   */
  protected abstract next (): void

  /**
   * Flag that indicates if approach is lazy or not (whether to preload on first render or only when attempting to trigger the list)
   */
  protected abstract lazy: boolean

  /**
   * Returns item, tries to fetch it from a special pool
   */
  protected abstract fetchItemByValue (value: T): V.Select.Item<T>

  /**
   * Indicates whether next is loading or not
   */
  protected nextLoading = ref(false)

  /**
   * Returns item if item with value exists
   */
  protected findItemByValue (value: T | null | undefined): V.Select.Item<T> | undefined {
    return findSelectOption(value, 'value', this.allOptions.value)
  }

  /**
   * Returns item if item with label exists
   */
  protected findItemByLabel (label: string): V.Select.Item<T> | undefined {
    return findSelectOption(label as T, 'label', this.allOptions.value)
  }

  /**
   * Use as composable
   */
  use () {
    return {
      options: this.options,
      /**
       * Term to search options by
       */
      search: this.search,
      /**
       * Direction to order options by
       */
      sort: this.sort,
      /**
       * Array of options
       */
      allOptions: this.allOptions,
      /**
       * Array of options scoped to the current search & sort settings
       */
      scopedOptions: this.scopedOptions,
      /**
       * Returns item if item with label exists
       */
      findItemByLabel: this.findItemByLabel.bind(this),
      /**
       * Returns item if item with value exists
       */
      findItemByValue: this.findItemByValue.bind(this),
      /**
       * Returns item, tries to fetch it from a special pool
       */
      fetchItemByValue: this.fetchItemByValue.bind(this),
      /**
       * Loads next page for options
       */
      next: this.next.bind(this),
      /**
       * Indicates whether next is an async operation
       */
      nextLoading: this.nextLoading,
      /**
       * Flag that indicates if approach is lazy or not (whether to preload on first render or only when attempting to trigger the list)
       */
      lazy: this.lazy
    }
  }
}

/**
 * Class used to create a standard select options provider. Accepts computed array of select options.
 */
export class StaticSelectOptionsProvider <T> extends SelectOptionsProvider <T> {
  lazy = false

  /**
   * useSearch instance (used since includeNestedProperty and exclude isn't supported by other search methods ATM)
   */
  private readonly searchInstance = useSearch(
    computed(() => this.toRecursiveSorted(this.allOptions.value, this.sort.value)),
    ['label', 'value'],
    toRef(false),
    {
      // @ts-expect-error TS2322
      includeNestedProperty: 'options',
      exclude: (object) => 'type' in object
    }
  )

  /**
   * Array of options scoped to the current search & sort settings
   */
  scopedOptions = computed(() => this.searchInstance.searchCollection.value)

  constructor (settings: { options: Ref<V.Select.Option<T>[]> }) {
    super()

    // Override options with a computed options from param
    this.options = settings.options

    // Overwrite search model
    this.search = this.searchInstance.searchModel
  }

  /**
   * No pagination is present
   */
  next () {}

  /**
   * Just return plain option back 
   */
  fetchItemByValue (value: T) {
    return { value }
  }

  /**
   * Method to recursively sort select options
   */
  private toRecursiveSorted (options: V.Select.Option<T>[], direction: 'asc' | 'desc' | undefined) {
    if (!direction) return options

    // Result
    const result: V.Select.Option<T>[] = []

    // Options that are queued for sorting (imagine block of consecutive options)
    const pending: (V.Select.Item<T> | V.Select.ItemGroup<T>)[] = []

    const sortPending = () => {
      if (pending.length === 0) return

      pending.sort((option1, option2) => {
        const label1 = String('type' in option1 ? option1.label : option1.label ?? option1.value)
        const label2 = String('type' in option2 ? option2.label : option2.label ?? option2.value)

        return direction === 'asc' ? label1.localeCompare(label2) : label2.localeCompare(label1)
      })

      result.push(...pending)

      pending.length = 0
    }

    for (const option of options) {
      // Yes the type in option could be ternarized but TS doesn't like it
      if (!('type' in option) || ('type' in option && option.type === 'group')) {
        pending.push(option)
      } else {
        sortPending()

        result.push(option)
      }
    }

    sortPending()

    return result
  }
}

/**
 * Class used to create a remote select options provider. Accepts an endpoint that is expected to handle `search`, `page` and `sort_direction` query params and returns `{ items: V.Select.Item<?>[] }`
 */
export class RemoteSelectOptionsProvider<T, U = V.Select.Option<T>> extends SelectOptionsProvider<T> {
  lazy = true

  scopedOptions = computed(() => this.allOptions.value)

  private readonly endpoint: PathHelper
  private readonly endpointParams: Parameters<PathHelper>[0]

  private readonly transform: (object: U) => V.Select.Option<T>

  /**
   * This map is used to keep track of the preloaded options that wouldnt normally display due to request pagination
   */
  private readonly preloadedOptions: Ref<Map<T, true | V.Select.Item<T>>>

  /**
   * Ref used to track the next page we should query, undefined for no page loaded yet
   */
  private page: number | undefined = undefined
  private nextLastCalled = 0

  constructor (settings: { endpoint: PathHelper, endpointParams?: Parameters<PathHelper>[0], transform?: (object: U) => V.Select.Option<T> }) {
    super()

    this.endpoint = settings.endpoint
    this.endpointParams = settings.endpointParams

    this.transform = settings.transform ?? ((item) => item as V.Select.Option<T>)

    this.preloadedOptions = ref(new Map())

    watch([this.search, this.sort], () => {
      this.page = undefined
      this.options.value = []

      void this.next()
    })
  }

  async prefetchItemsByValue (values: T[]) {
    for (const value of values) {
      this.preloadedOptions.value.set(value, true)
    }

    const { items } = await this.endpoint<{ items: U[] }>({
      ...this.endpointParams,
      params: {
        ...this.endpointParams?.params,
        query: {
          ...(this.endpointParams?.params as undefined | { query: Record<string, string> })?.query,
          exact: values
        }
      }
    })

    for (const item of items) {
      const option = this.transform(item) as V.Select.Item<T>

      this.preloadedOptions.value.set(option.value, option)
    }
  }

  fetchItemByValue (value: T) {
    const loadedOption = this.preloadedOptions.value.get(value)

    if (loadedOption) {
      // Return self or preloaded option
      return loadedOption === true ? { value } : loadedOption
    } else {
      // Flag as loading
      this.preloadedOptions.value.set(value, true)

      void (async () => {
        const { items } = await this.endpoint<{ items: U[] }>({
          ...this.endpointParams,
          params: {
            ...this.endpointParams?.params,
            query: {
              ...(this.endpointParams?.params as undefined | { query: Record<string, string> })?.query,
              exact: value
            }
          }
        })

        if (items.length > 0) {
          this.preloadedOptions.value.set(value, this.transform(items[0]) as V.Select.Item<T>)
        } else {
          this.preloadedOptions.value.set(value, { value })
        }
      })()

      return { value }
    }
  }

  next () {
    const nextCalledAt = this.nextLastCalled = Date.now()

    void (async () => {
      try {
        this.nextLoading.value = true

        const { items } = await this.endpoint<{ items: U[] }>({
          ...this.endpointParams,
          params: {
            ...this.endpointParams?.params,
            query: {
              ...(this.endpointParams?.params as undefined | { query: Record<string, string> })?.query,
              page: this.page ?? 1,
              search: this.search.value,
              sort_direction: this.sort.value
            }
          }
        })

        const options = items.map((item) => this.transform(item))

        if (this.nextLastCalled !== nextCalledAt) return

        if (typeof this.page === 'undefined') {
          this.options.value = options
          this.page = 2
        } else {
          this.options.value.push(...options)
          this.page += 1
        }
      } catch (e) {
        useErrorToast(e)
      } finally {
        this.nextLoading.value = false
      }
    })()
  }
}